import { faTimeline, faUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ColDef } from 'ag-grid-community';

import {
  InsuranceVerificationListItem,
  InsuranceVerificationStatus
} from '@/types/insuranceVerification';
import Tooltip from '@/ui/components/Tooltip/Tooltip';

const APPLY_FILTER_PARAMS = {
  buttons: ['reset', 'apply'],
  closeOnApply: true
};

const EQUALS_FILTER_PARAMS = {
  ...APPLY_FILTER_PARAMS,
  filterOptions: ['equals'],
  maxNumConditions: 5,
  defaultJoinOperator: 'OR'
};

const CONTAINS_ANY_FILTER_PARAMS = {
  ...EQUALS_FILTER_PARAMS,
  filterOptions: ['contains']
};

const CONTAINS_FILTER_PARAMS = {
  ...APPLY_FILTER_PARAMS,
  filterOptions: ['contains'],
  maxNumConditions: 1
};

const localCalendarDay = (value: string): Date => {
  const [year, month, day] = value.slice(0, 10).split('-').map(Number);
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return new Date(year, month - 1, day);

  const instant = new Date(value);
  return new Date(instant.getFullYear(), instant.getMonth(), instant.getDate());
};

export const compareCellDayToFilterDay = (
  filterLocalDateAtMidnight: Date,
  cellValue: string | null
): number => {
  if (cellValue == null) return -1;
  const cellDay = localCalendarDay(cellValue).getTime();
  const filterDay = filterLocalDateAtMidnight.getTime();
  if (cellDay < filterDay) return -1;
  if (cellDay > filterDay) return 1;
  return 0;
};

const DATE_FILTER_PARAMS = {
  ...APPLY_FILTER_PARAMS,
  filterOptions: ['equals', 'greaterThan', 'lessThan', 'inRange'],
  maxNumConditions: 1,
  inRangeInclusive: true,
  comparator: compareCellDayToFilterDay
};

const dateCellRenderer = (params: { data?: unknown; value?: string }) => {
  if (!params.data) return '';
  return params.value ? new Date(params.value).toLocaleDateString() : '-';
};

const daysSinceVerifiedCellRenderer = (params: { data?: unknown; value?: number | null }) => {
  if (!params.data) return '';
  const days = params.value;
  if (days === null || days === undefined) return '-';

  const badgeColor =
    days >= 60
      ? 'bg-red-100 text-red-800'
      : days >= 30
        ? 'bg-amber-100 text-amber-800'
        : 'bg-green-100 text-green-800';

  return (
    <span
      className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full status-badge ${badgeColor}`}>
      {days}d
    </span>
  );
};

const parsePlanDate = (value?: string | null): Date | null => {
  if (!value) return null;

  const [year, month, day] = value.split('-').map((part) => Number.parseInt(part, 10));
  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day);
};

const planStartCellRenderer = (params: { data?: unknown; value?: string | null }) => {
  if (!params.data) return '';
  const planStart = parsePlanDate(params.value);

  return planStart ? planStart.toLocaleDateString() : '-';
};

const planEndCellRenderer = (params: { data?: unknown; value?: string | null }) => {
  if (!params.data) return '';
  const planEnd = parsePlanDate(params.value);
  if (!planEnd) return '-';

  const hasEnded = planEnd.getTime() < new Date().setHours(0, 0, 0, 0);

  return (
    <span className={hasEnded ? 'text-red-700 font-semibold' : ''}>
      {planEnd.toLocaleDateString()}
    </span>
  );
};

const policyDeletedCellRenderer = (params: { data?: unknown; value?: string | null }) => {
  if (!params.data) return '';
  if (!params.value) return '-';

  return (
    <Tooltip tooltipContent={`Policy deleted ${new Date(params.value).toLocaleDateString()}`}>
      <span className="inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full status-badge bg-red-100 text-red-800">
        Deleted
      </span>
    </Tooltip>
  );
};

const TIMELINE_DEFAULT_MONTHS = 6;

export const billingTimelineHref = (verification: InsuranceVerificationListItem): string => {
  const params = new URLSearchParams();
  if (verification.insurance_policy_id) params.set('policy', verification.insurance_policy_id);

  if (verification.verified_at) {
    params.set('event', `insurance_verification:${verification.id}`);

    const timelineStart = new Date();
    timelineStart.setMonth(timelineStart.getMonth() - TIMELINE_DEFAULT_MONTHS);
    const dayBefore = new Date(verification.verified_at);
    dayBefore.setDate(dayBefore.getDate() - 1);
    if (dayBefore < timelineStart) params.set('start', dayBefore.toISOString().split('T')[0]);
  }

  const search = params.toString();
  return `/people/${verification.member_id}/billing/timeline${search ? `?${search}` : ''}`;
};

const memberCellRenderer = (params: { data?: unknown; canViewBillingTimeline?: boolean }) => {
  const verification = params.data as InsuranceVerificationListItem;
  if (!verification) return '';
  const firstName = verification.member_first_name || '';
  const lastName = verification.member_last_name || '';
  const fullName = `${firstName} ${lastName}`.trim();
  const memberId = verification.member_id;

  // Profile link — unchanged: name links to the dugout person profile.
  const nameNode =
    memberId && fullName ? (
      <a href={`/people/${memberId}`} className="mp-sensitive text-blue-600 hover:text-blue-800">
        {fullName}
      </a>
    ) : (
      <span>{fullName || '-'}</span>
    );

  if (!memberId) {
    return nameNode;
  }

  // Name keeps the in-place profile link; the trailing icons are compact actions:
  // open the profile in a new tab, and open this row's VOB on the billing timeline.
  return (
    <span className="flex items-center gap-2 min-w-0">
      <span className="truncate min-w-0">{nameNode}</span>
      <span className="flex items-center gap-1.5 flex-none text-gray-400">
        <a
          href={`/people/${memberId}`}
          target="_blank"
          rel="noopener noreferrer"
          title="Open profile in new tab"
          aria-label="Open profile in new tab"
          className="hover:text-blue-600">
          <FontAwesomeIcon icon={faUpRightFromSquare} className="h-3 w-3" />
        </a>
        {params.canViewBillingTimeline && (
          <>
            <span className="h-3 w-px bg-gray-300" aria-hidden="true" />
            <a
              href={billingTimelineHref(verification)}
              target="_blank"
              rel="noopener noreferrer"
              title="Billing & VOB timeline"
              aria-label="Billing & VOB timeline"
              className="hover:text-blue-600">
              <FontAwesomeIcon icon={faTimeline} className="h-3 w-3" />
            </a>
          </>
        )}
      </span>
    </span>
  );
};

const verifiedByCellRenderer = (params: { data?: unknown }) => {
  const verification = params.data as InsuranceVerificationListItem;
  if (!verification) return '';
  return verification.verified_by
    ? `${verification.verified_by.first_name} ${verification.verified_by.last_name}`
    : '-';
};

const formatCurrencyFromCents = (value?: number | null) => {
  if (value === null || value === undefined) return '-';
  const dollars = value / 100;
  return dollars.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const serviceCodeCellRenderer = (params: { data?: unknown; value?: string }) => {
  if (!params.data) return '';
  const serviceCode = params.value;
  if (!serviceCode) return '-';
  return (
    <span className="inline-flex px-1 py-0 text-[10px] leading-tight font-mono font-semibold rounded bg-amber-100 text-amber-800 border border-amber-200 truncate service-code-badge">
      {serviceCode}
    </span>
  );
};

const statusCellRenderer = (params: { data?: unknown; value?: InsuranceVerificationStatus }) => {
  if (!params.data) return '';
  const status = params.value as InsuranceVerificationStatus;
  const getStatusBadgeColor = (s: InsuranceVerificationStatus) => {
    switch (s) {
      case InsuranceVerificationStatus.VERIFIED:
        return 'bg-green-100 text-green-800';
      case InsuranceVerificationStatus.BILLING_REQUIREMENTS_MET:
        return 'bg-blue-100 text-blue-800';
      case InsuranceVerificationStatus.PENDING:
        return 'bg-yellow-100 text-yellow-800';
      case InsuranceVerificationStatus.FAILED:
        return 'bg-red-100 text-red-800';
      case InsuranceVerificationStatus.PENDING_COB:
        return 'bg-amber-100 text-amber-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return (
    <span
      className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full status-badge ${getStatusBadgeColor(
        status
      )}`}>
      {status}
    </span>
  );
};

const memberStatusCellRenderer = (params: { data?: unknown; value?: string }) => {
  if (!params.data) return '';
  const memberStatus = params.value;
  if (!memberStatus) return '-';

  const getMemberStatusBadgeColor = (s: string) => {
    switch (s.toUpperCase()) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800';
      case 'DISCHARGED':
        return 'bg-red-100 text-red-800';
      case 'OTHER':
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatMemberStatus = (s: string) => {
    return s
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <span
      className={`inline-flex px-1.5 py-0.5 text-xs font-semibold rounded-full status-badge ${getMemberStatusBadgeColor(
        memberStatus
      )}`}>
      {formatMemberStatus(memberStatus)}
    </span>
  );
};

export const createColumnDefs = ({ canViewBillingTimeline = false } = {}): ColDef[] => [
  {
    headerName: 'Member ID',
    field: 'member_id',
    minWidth: 120,
    width: 140,
    sortable: false,
    filter: 'agTextColumnFilter',
    filterParams: EQUALS_FILTER_PARAMS,
    resizable: true,
    headerClass: 'ag-header-prominent',
    cellClass: 'ag-cell-member-id',
    valueGetter: (params) => {
      const verification = params.data as InsuranceVerificationListItem;
      return verification?.member_id || '-';
    }
  },
  {
    headerName: 'Member',
    field: 'member_name',
    cellRenderer: memberCellRenderer as unknown as ColDef['cellRenderer'],
    cellRendererParams: { canViewBillingTimeline },
    minWidth: 180,
    width: 200,
    sortable: true,
    filter: 'agTextColumnFilter',
    filterParams: CONTAINS_ANY_FILTER_PARAMS,
    resizable: true,
    headerClass: 'ag-header-prominent',
    cellClass: 'ag-cell-member',
    valueGetter: (params) => {
      const verification = params.data as InsuranceVerificationListItem;
      if (!verification) return '';
      return `${verification.member_first_name || ''} ${verification.member_last_name || ''}`.trim();
    }
  },
  {
    headerName: 'Member Status',
    field: 'member_status',
    cellRenderer: memberStatusCellRenderer as unknown as ColDef['cellRenderer'],
    minWidth: 140,
    width: 160,
    sortable: true,
    filter: 'agTextColumnFilter',
    filterParams: EQUALS_FILTER_PARAMS,
    resizable: true,
    headerClass: 'ag-header-prominent',
    cellClass: 'ag-cell-member-status',
    valueFormatter: (params) => params.value || '-'
  },
  {
    headerName: 'Jurisdiction',
    field: 'member_jurisdiction',
    minWidth: 140,
    width: 160,
    sortable: true,
    filter: false,
    resizable: true,
    headerClass: 'ag-header-prominent',
    cellClass: 'ag-cell-jurisdiction',
    valueFormatter: (params) => params.value || '-'
  },
  {
    headerName: 'Policy Carrier',
    field: 'policy_carrier',
    minWidth: 280,
    width: 340,
    sortable: true,
    filter: false,
    resizable: true,
    headerClass: 'ag-header-prominent',
    cellClass: 'ag-cell-carrier ag-cell-ellipsis',
    valueFormatter: (params) => params.value || '-'
  },
  {
    headerName: 'Plan',
    field: 'policy_plan',
    minWidth: 220,
    width: 260,
    sortable: true,
    filter: false,
    resizable: true,
    headerClass: 'ag-header-prominent',
    cellClass: 'ag-cell-plan ag-cell-ellipsis',
    valueFormatter: (params) => params.value || '-'
  },
  {
    headerName: 'Policy Priority',
    field: 'policy_priority_type',
    minWidth: 180,
    width: 200,
    sortable: true,
    filter: 'agTextColumnFilter',
    filterParams: EQUALS_FILTER_PARAMS,
    resizable: true,
    headerClass: 'ag-header-prominent',
    cellClass: 'ag-cell-policy-priority',
    valueFormatter: (params) => params.value || '-'
  },
  {
    headerName: 'Plan Type',
    field: 'policy_type',
    minWidth: 130,
    width: 140,
    sortable: false,
    filter: 'agTextColumnFilter',
    filterParams: EQUALS_FILTER_PARAMS,
    resizable: true,
    headerClass: 'ag-header-prominent',
    cellClass: 'ag-cell-plan-type',
    valueFormatter: (params) => params.value || '-'
  },
  {
    headerName: 'Payer Member ID',
    field: 'policy_member_id',
    minWidth: 170,
    width: 180,
    sortable: false,
    filter: 'agTextColumnFilter',
    filterParams: EQUALS_FILTER_PARAMS,
    resizable: true,
    headerClass: 'ag-header-prominent',
    cellClass: 'ag-cell-payer-member-id',
    valueFormatter: (params) => params.value || '-'
  },
  {
    headerName: 'Subscriber ID',
    field: 'policy_subscriber_id',
    minWidth: 160,
    width: 170,
    sortable: false,
    filter: 'agTextColumnFilter',
    filterParams: EQUALS_FILTER_PARAMS,
    resizable: true,
    headerClass: 'ag-header-prominent',
    cellClass: 'ag-cell-subscriber-id',
    valueFormatter: (params) => params.value || '-'
  },
  {
    headerName: 'Group ID',
    field: 'policy_group_id',
    minWidth: 140,
    width: 150,
    sortable: false,
    filter: 'agTextColumnFilter',
    filterParams: EQUALS_FILTER_PARAMS,
    resizable: true,
    headerClass: 'ag-header-prominent',
    cellClass: 'ag-cell-group-id',
    valueFormatter: (params) => params.value || '-'
  },
  {
    headerName: 'Policy Verification',
    field: 'policy_verification_status',
    minWidth: 200,
    width: 210,
    sortable: false,
    filter: 'agTextColumnFilter',
    filterParams: EQUALS_FILTER_PARAMS,
    resizable: true,
    headerClass: 'ag-header-prominent',
    cellClass: 'ag-cell-policy-verification',
    valueFormatter: (params) => params.value || '-'
  },
  {
    headerName: 'Status',
    field: 'status',
    cellRenderer: statusCellRenderer as unknown as ColDef['cellRenderer'],
    minWidth: 160,
    width: 180,
    sortable: true,
    filter: 'agTextColumnFilter',
    filterParams: EQUALS_FILTER_PARAMS,
    resizable: true,
    headerClass: 'ag-header-prominent',
    cellClass: 'ag-cell-status'
  },
  {
    headerName: 'Verified At',
    field: 'verified_at',
    cellRenderer: dateCellRenderer as unknown as ColDef['cellRenderer'],
    minWidth: 140,
    width: 160,
    sortable: true,
    sort: 'desc',
    filter: 'agDateColumnFilter',
    filterParams: DATE_FILTER_PARAMS,
    resizable: true,
    headerClass: 'ag-header-prominent',
    cellClass: 'ag-cell-verified-at'
  },
  {
    headerName: 'Days Since VOB',
    field: 'days_since_verified',
    cellRenderer: daysSinceVerifiedCellRenderer as unknown as ColDef['cellRenderer'],
    minWidth: 140,
    width: 150,
    sortable: true,
    filter: false,
    resizable: true,
    headerClass: 'ag-header-prominent',
    cellClass: 'ag-cell-days-since-vob'
  },
  {
    headerName: 'Deleted',
    field: 'policy_deleted_at',
    cellRenderer: policyDeletedCellRenderer as unknown as ColDef['cellRenderer'],
    minWidth: 100,
    width: 110,
    sortable: false,
    filter: false,
    resizable: true,
    headerClass: 'ag-header-prominent',
    cellClass: 'ag-cell-policy-deleted'
  },
  {
    headerName: 'Plan Start',
    field: 'policy_start_date',
    cellRenderer: planStartCellRenderer as unknown as ColDef['cellRenderer'],
    minWidth: 120,
    width: 130,
    sortable: false,
    filter: 'agDateColumnFilter',
    filterParams: DATE_FILTER_PARAMS,
    resizable: true,
    headerClass: 'ag-header-prominent',
    cellClass: 'ag-cell-plan-start'
  },
  {
    headerName: 'Plan End',
    field: 'policy_end_date',
    cellRenderer: planEndCellRenderer as unknown as ColDef['cellRenderer'],
    minWidth: 120,
    width: 130,
    sortable: false,
    filter: 'agDateColumnFilter',
    filterParams: DATE_FILTER_PARAMS,
    resizable: true,
    headerClass: 'ag-header-prominent',
    cellClass: 'ag-cell-plan-end'
  },
  {
    headerName: 'Policy Created At',
    field: 'policy_created_at',
    cellRenderer: dateCellRenderer as unknown as ColDef['cellRenderer'],
    minWidth: 180,
    width: 200,
    sortable: true,
    filter: 'agDateColumnFilter',
    filterParams: DATE_FILTER_PARAMS,
    resizable: true,
    headerClass: 'ag-header-prominent',
    cellClass: 'ag-cell-policy-created'
  },
  {
    headerName: 'Service Code',
    field: 'service_code',
    cellRenderer: serviceCodeCellRenderer as unknown as ColDef['cellRenderer'],
    minWidth: 120,
    width: 140,
    sortable: true,
    filter: 'agTextColumnFilter',
    filterParams: EQUALS_FILTER_PARAMS,
    resizable: true,
    headerClass: 'ag-header-prominent',
    cellClass: 'ag-cell-service-code',
    valueFormatter: (params) => params.value || '-'
  },
  {
    headerName: 'Verified By',
    field: 'verified_by',
    filterValueGetter: (params) => {
      const staff = (params.data as InsuranceVerificationListItem)?.verified_by;
      return staff ? `${staff.first_name} ${staff.last_name} ${staff.email ?? ''}`.trim() : '';
    },
    cellRenderer: verifiedByCellRenderer as unknown as ColDef['cellRenderer'],
    minWidth: 160,
    width: 180,
    sortable: true,
    filter: 'agTextColumnFilter',
    filterParams: CONTAINS_FILTER_PARAMS,
    resizable: true,
    headerClass: 'ag-header-prominent',
    cellClass: 'ag-cell-verified-by',
    valueGetter: (params) => {
      const verification = params.data as InsuranceVerificationListItem;
      if (!verification) return '';
      return verification.verified_by
        ? `${verification.verified_by.first_name} ${verification.verified_by.last_name}`
        : '';
    }
  },
  {
    headerName: 'Verified Through',
    field: 'verified_through',
    cellRenderer: dateCellRenderer as unknown as ColDef['cellRenderer'],
    minWidth: 180,
    width: 200,
    sortable: true,
    filter: 'agDateColumnFilter',
    filterParams: DATE_FILTER_PARAMS,
    resizable: true,
    headerClass: 'ag-header-prominent',
    cellClass: 'ag-cell-verified-through'
  },
  {
    headerName: 'Verified Via',
    field: 'verified_via',
    minWidth: 160,
    width: 180,
    sortable: false,
    filter: 'agTextColumnFilter',
    filterParams: EQUALS_FILTER_PARAMS,
    resizable: true,
    headerClass: 'ag-header-prominent',
    cellClass: 'ag-cell-verified-via',
    valueGetter: (params) => {
      const vv = (params.data as InsuranceVerificationListItem)?.verified_via as
        { name?: string } | string | undefined;
      if (!vv) return '-';
      if (typeof vv === 'string') return vv || '-';
      return vv.name || '-';
    }
  },
  {
    headerName: 'Vendor',
    colId: 'external_verification_source',
    field: 'external_policy_verification.source',
    minWidth: 130,
    width: 140,
    sortable: false,
    filter: 'agTextColumnFilter',
    filterParams: EQUALS_FILTER_PARAMS,
    resizable: true,
    headerClass: 'ag-header-prominent',
    cellClass: 'ag-cell-vendor',
    valueGetter: (params) =>
      (params.data as InsuranceVerificationListItem)?.external_policy_verification?.source || '-'
  },
  {
    headerName: 'Check State',
    field: 'external_verification_state',
    minWidth: 130,
    width: 140,
    sortable: false,
    filter: 'agTextColumnFilter',
    filterParams: EQUALS_FILTER_PARAMS,
    resizable: true,
    headerClass: 'ag-header-prominent',
    cellClass: 'ag-cell-check-state',
    valueFormatter: (params) => params.value || '-'
  },
  {
    headerName: 'Deductible Remaining',
    field: 'deductible_remaining',
    minWidth: 160,
    width: 170,
    sortable: false,
    filter: false,
    resizable: true,
    headerClass: 'ag-header-prominent',
    cellClass: 'ag-cell-finance ag-right-aligned-cell',
    valueGetter: (params) =>
      formatCurrencyFromCents((params.data as InsuranceVerificationListItem)?.deductible_remaining)
  },
  {
    headerName: 'OOP Max Remaining',
    field: 'out_of_pocket_maximum_remaining_in_cents',
    minWidth: 170,
    width: 180,
    sortable: false,
    filter: false,
    resizable: true,
    headerClass: 'ag-header-prominent',
    cellClass: 'ag-cell-finance ag-right-aligned-cell',
    valueGetter: (params) =>
      formatCurrencyFromCents(
        (params.data as InsuranceVerificationListItem)?.out_of_pocket_maximum_remaining_in_cents
      )
  },
  {
    headerName: 'Est. Monthly Cost',
    field: 'estimated_monthly_cost_in_cents',
    minWidth: 160,
    width: 170,
    sortable: false,
    filter: false,
    resizable: true,
    headerClass: 'ag-header-prominent',
    cellClass: 'ag-cell-finance ag-right-aligned-cell',
    valueGetter: (params) =>
      formatCurrencyFromCents(
        (params.data as InsuranceVerificationListItem)?.estimated_monthly_cost_in_cents
      )
  },
  {
    headerName: 'Est. Initial Month Cost',
    field: 'estimated_initial_month_cost_in_cents',
    minWidth: 200,
    width: 220,
    sortable: false,
    filter: false,
    resizable: true,
    headerClass: 'ag-header-prominent',
    cellClass: 'ag-cell-finance ag-right-aligned-cell',
    valueGetter: (params) =>
      formatCurrencyFromCents(
        (params.data as InsuranceVerificationListItem)?.estimated_initial_month_cost_in_cents
      )
  },
  {
    headerComponent: (_params: unknown) => (
      <Tooltip tooltipContent="These values were determined by external verifications">
        <span>External Name*</span>
      </Tooltip>
    ),
    colId: 'external_name',
    field: 'external_policy_verification.external_name',
    minWidth: 250,
    width: 280,
    sortable: false,
    filter: 'agTextColumnFilter',
    filterParams: CONTAINS_FILTER_PARAMS,
    resizable: true,
    headerClass: 'ag-header-prominent',
    cellClass: 'ag-cell-external-desc',
    valueGetter: (params) => {
      const verification = params.data as InsuranceVerificationListItem;
      if (!verification) return '-';
      return verification.external_policy_verification?.external_name || '-';
    }
  },
  {
    headerComponent: (_params: unknown) => (
      <Tooltip tooltipContent="These values were determined by external verifications">
        <span>Eligibility Status*</span>
      </Tooltip>
    ),
    field: 'eligibility_status',
    minWidth: 180,
    width: 200,
    sortable: false,
    filter: 'agTextColumnFilter',
    filterParams: CONTAINS_FILTER_PARAMS,
    resizable: true,
    headerClass: 'ag-header-prominent',
    cellClass: 'ag-cell-eligibility',
    valueGetter: (params) =>
      (params.data as InsuranceVerificationListItem)?.external_policy_verification?.eligibility
        ?.eligibility_status_description || '-'
  },
  {
    headerComponent: (_params: unknown) => (
      <Tooltip tooltipContent="These values were determined by external verifications">
        <span>Coverage Type*</span>
      </Tooltip>
    ),
    field: 'coverage_type',
    minWidth: 150,
    width: 170,
    sortable: false,
    filter: false,
    resizable: true,
    headerClass: 'ag-header-prominent',
    cellClass: 'ag-cell-coverage',
    valueGetter: (params) => {
      const evp = (params.data as InsuranceVerificationListItem)?.external_policy_verification;
      const first = evp?.coverages && evp.coverages.length > 0 ? evp.coverages[0] : undefined;
      return first?.insurance_type || first?.plan_name || '-';
    }
  }
];
